import React from "react";
import { useState, useEffect } from "react";

export type IconSvgProps = {
  /** Sets the url for the component to grab the icon from  */
  svgUrl?: string;
  /** Sets the color of the svg */
  color?: string | "currentColor";
  /** Sets the accent color of the svg */
  accentColor?: boolean;
  /** Determines if the icon is a pictogram */
  pictogram?: boolean;
  /** icon size XXL | XXL | XL | LG | MD | SM | XS */
  size?: IconSvgSizes;
  /** svg as string */
  svgNodeStr?: string;
  /** image url as string */
  imgUrl?: string;
  /**To add alt text to icon svg */
  altText?: string;
};

export enum IconSvgSizes {
  XXXL = "128",
  XXL = "96",
  XL = "64",
  LG = "48",
  MD = "32",
  SM = "24",
  XS = "16",
  LINK = "20",
}

IconSvg.defaultProps = {
  color: "",
  accentColor: false,
  size: IconSvgSizes.MD,
  pictogram: false,
};

export function IconSvg({
  svgUrl,
  svgNodeStr,
  color,
  accentColor,
  pictogram,
  size,
  imgUrl,
  altText,
  ...props
}: IconSvgProps & React.SVGAttributes<SVGElement>) {
  const ERROR_MSG =
    "Invalid sizes are passed. Functional icons should have Sizes: 16,24,32,48,64 px only (may add 20px) & Pictograms are valid for Sizes: 48, 64, 96 only";
  /* stateful items */
  const [errorMsg, setErrorMsg] = useState("");
  const [svgContent, setSvgContent] = useState({
    body: undefined,
    viewBox: undefined,
  });

  let svgContentTmp;
   /* asynchronous after render - only execute on state change for... */
  useEffect(() => {
    if (!svgUrl) return;
    fetch(svgUrl)
      .then((response) => response.text())
      .then(
        /* istanbul ignore next */ (response) => {
          const svgContent = processSvgContent(response);
          setSvgContent({
            body: svgContent.body,
            viewBox: svgContent.viewBox,
          });
        }
      )
      .catch((err) => {
        setSvgContent(undefined);
      });
  }, [svgUrl]);


  const processSvgContent = (svgStr: string) => {
    // Strip the SVG tags
    const trimmedStr = svgStr.trim();
    const index = trimmedStr.indexOf(">");
    const body = trimmedStr.substring(index + 1, trimmedStr.length - 6);

    // get the viewbox
    // TODO make this more robust...
    const splitA = svgStr?.split('viewBox="');
    const splitB = splitA.length > 1 && splitA[1]?.split('"');
    const viewBox = splitB && splitB[0];

    return {body, viewBox}
  };

  if (svgNodeStr) {
    svgContentTmp = processSvgContent(svgNodeStr);
  }

  const customClass = accentColor
    ? `svg-accent-att-blue ${color || "color-black"}`
    : color;

  return (svgContentTmp?.body || svgContent?.body) && !errorMsg && !imgUrl ? (
    <svg
      {...{ 'aria-label': altText ? altText : undefined }}
      {...props}
      focusable="false"
      className={
        props.className ? props.className + " " + customClass : customClass
      }
      height={size}
      width={size}
      viewBox={svgNodeStr ? svgContentTmp.viewBox : svgContent.viewBox}
      dangerouslySetInnerHTML={{
        __html: svgNodeStr ? svgContentTmp.body : svgContent.body,
      }}
    />
  ) : (svgNodeStr || svgUrl) && !errorMsg && !imgUrl ? (
    <>
      <noscript>
        {svgNodeStr && (
          <div
            className={
              props.className
                ? props.className + " " + customClass
                : customClass
            }
            dangerouslySetInnerHTML={{ __html: svgNodeStr }}
          />
        )}
        {svgUrl && (
          <img
            src={svgUrl}
            alt={altText && altText !== undefined ? altText : "icon image"}
            height={size}
            width={size}
            className={
              props.className
                ? props.className + " " + customClass
                : customClass
            }
          />
        )}
      </noscript>
    </>
  ) : /* istanbul ignore next */ errorMsg ? (
    <div
      className="flex flex-items-stretch pad-t-xxs pad-b-xxs rel"
      role="alert"
    >
      <p className="type-sm line-h-lg mar-t-1-sm">{ERROR_MSG}</p>
    </div>
  ) : imgUrl && !errorMsg ? (
    <img
      src={imgUrl}
      alt={altText && altText !== undefined ? altText : "icon image"}
      className="mar-r-xs radius-xs"
    />
  ) : null;
}
